import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const MENU_ITEMS = [
  { label: 'Forside', href: '/' },
  {
    label: 'Ydelser',
    href: '/ydelser',
    children: [
      { label: 'Græsslåning', href: '/ydelser/graesslaaning' },
      { label: 'Ny terrasse', href: '/ydelser/ny-terrasse' },
      { label: 'Ny indkørsel', href: '/ydelser/ny-indkoersel' },
      { label: 'Etablering af græsplæner', href: '/ydelser/etablering-af-graesplaener' },
      { label: 'Brolægning', href: '/ydelser/brolaegning' }
    ]
  },
  { label: 'Galleri', href: '/galleri' },
  { label: 'Om os', href: '/om-os' },
  { label: 'Kontakt', href: '/kontakt' }
];

export default function Header() {
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  const isActive = (href) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b border-[--color-brand-sand] shadow-xs">
      {/* Top Bar with Contact Info */}
      <div className="bg-[--color-brand-green-medium] text-[#BCC8B3] py-2 px-6 border-b border-[--color-brand-green-dark]/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 text-xs font-sans tracking-wide">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="fa-solid fa-location-dot text-[#BCC8B3]"></i>
              Søndergade 111, 5591 Gelsted
            </span>
            <span className="hidden md:inline text-[#BCC8B3]">|</span>
            <span className="hidden md:inline text-[#BCC8B3]">CVR: 32127347</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:30441611" className="flex items-center gap-1.5 hover:text-[#BCC8B3] transition-colors whitespace-nowrap text-[#BCC8B3]">
              <i className="fa-solid fa-phone text-[#BCC8B3]"></i>
              30 44 16 11
            </a>
            <a href="mailto:post@bp-haveservice.dk" className="flex items-center gap-1.5 hover:text-[#BCC8B3] transition-colors whitespace-nowrap text-[#BCC8B3]">
              <i className="fa-solid fa-envelope text-[#BCC8B3]"></i>
              post@bp-haveservice.dk
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/logo.svg"
            alt="BP Totalservice Logo"
            className="h-10 md:h-12 w-auto"
          />
        </Link>

        {/* Desktop Navigation (Visible on screens >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {MENU_ITEMS.map((item, idx) => {
            if (item.children) {
              return (
                <div
                  key={idx}
                  className="relative"
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1.5 font-sans text-sm font-semibold tracking-wide py-2 cursor-pointer transition-colors text-[#BCC8B3] hover:text-[#BCC8B3]`}
                  >
                    {item.label}
                    <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Dropdown Menu */}
                  {servicesDropdownOpen && (
                    <div className="absolute top-full left-0 w-64 bg-white border border-[--color-brand-sand] rounded-2xl shadow-lg py-3 z-50 animate-fade-in">
                      {item.children.map((child, cIdx) => (
                        <Link
                          key={cIdx}
                          to={child.href}
                          className={`block px-6 py-2.5 font-sans text-sm font-medium transition-colors text-[#BCC8B3] hover:bg-[--color-brand-cream]/50 hover:text-[#BCC8B3] ${
                            pathname === child.href ? 'bg-[--color-brand-cream] font-semibold' : ''
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={idx}
                to={item.href}
                className={`font-sans text-sm font-semibold tracking-wide py-2 transition-colors relative text-[#BCC8B3] hover:text-[#BCC8B3]`}
              >
                {item.label}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#BCC8B3] rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* CTA Button (Desktop) */}
        <div className="hidden min-[1200px]:block">
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-[#BDC8B3] text-[--color-brand-green-dark] hover:bg-[--color-brand-green-dark] hover:text-white transition-colors duration-200 text-sm font-semibold tracking-wide shadow-xs cursor-pointer"
          >
            Få Et Tilbud
          </Link>
        </div>

        {/* Mobile Burgermenu Button (Visible on screens < 1200px) */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex min-[1200px]:hidden w-10 h-10 items-center justify-center rounded-full border border-[--color-brand-sand] text-[#BCC8B3] hover:bg-[--color-brand-cream] transition-colors cursor-pointer"
          aria-label="Menu"
        >
          <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}></i>
        </button>
      </div>

      {/* Mobile Menu Panel (Visible on screens < 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-[--color-brand-sand] bg-white px-6 py-6 space-y-4 animate-fade-in">
          <nav className="flex flex-col space-y-3">
            {MENU_ITEMS.map((item, idx) => {
              if (item.children) {
                return (
                  <div key={idx} className="space-y-2">
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`flex w-full items-center justify-between font-sans text-base font-semibold py-1.5 cursor-pointer text-[#BCC8B3]`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                    </button>

                    {/* Mobile Dropdown Items */}
                    {mobileServicesOpen && (
                      <div className="pl-4 border-l border-[--color-brand-sand] space-y-2 py-1">
                        {item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.href}
                            className={`block font-sans text-sm py-1.5 transition-colors text-[#BCC8B3] hover:text-[#BCC8B3] ${
                              pathname === child.href ? 'font-semibold' : ''
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`block font-sans text-base font-semibold py-1.5 transition-colors text-[#BCC8B3] hover:text-[#BCC8B3]`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile CTA */}
          <div className="pt-4 border-t border-[--color-brand-sand] flex flex-col gap-3">
            <Link
              to="/kontakt"
              className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#BDC8B3] text-[--color-brand-green-dark] hover:bg-[--color-brand-green-dark] hover:text-white transition-colors text-sm font-semibold tracking-wide text-center"
            >
              Få Et Tilbud
            </Link>
            <a
              href="tel:30441611"
              className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full border border-[--color-brand-sand] text-[#BCC8B3] hover:bg-[--color-brand-cream] transition-colors text-sm font-semibold tracking-wide text-center"
            >
              Ring: 30 44 16 11
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
